import {css, CSSProperties, StyleSheet} from 'aphrodite';
import React from 'react';

import isArchived from 'archive/isArchived';
import {Owner} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {EntitySummary} from 'constants/UserConstants';
import {ConnectionSchema} from 'instruments/connection-schemas/ConnectionSchemaConstants';
import SchemaNameInput from 'schemas/SchemaNameInput';
import SchemaOwnerDisplay from 'schemas/SchemaOwnerDisplay';
import SchemaPrefixInput from 'schemas/SchemaPrefixInput';
import SchemaWarehouseNameInput from 'schemas/SchemaWarehouseNameInput';

import AdapterSelector from './AdapterSelector';

// exported for test only
const baseTestAttrs = {component: 'ConnectionSchemaMetadataSection'};
export const DATA_TEST_ATTRS = {
  name: {...baseTestAttrs, element: 'schemaName'},
  owner: {...baseTestAttrs, element: 'ownerName'},
  warehouseTable: {...baseTestAttrs, element: 'warehouseTable'},
};

interface ConnectionSchemaMetadataSectionProps {
  shouldAllowSchemaUpdates: boolean;
  isEditableWarehouseTableName: boolean;
  hideWarehouseTableName?: boolean;
  adapterId?: string;
  name?: string;
  ownerId?: string;
  ownerChoices?: Array<Owner> | null;
  sqlIdentifier?: string | null;
  idPrefix?: string | null;
  schema?: ConnectionSchema | null;
  onAdapterIdChange: (adapterId: string) => void;
  onNameChange: (name: string) => void;
  onOwnerChange: (owner: EntitySummary) => void;
  onSqlIdentifierChange: (sqlIdentifier: string) => void;
  onIdPrefixChange: (idPrefix: string) => void;
}

export default function ConnectionSchemaMetadataSection(
  props: ConnectionSchemaMetadataSectionProps
): JSX.Element {
  const isSchemaArchived = props.schema != null && isArchived(props.schema);
  const isBeingCreated = props.schema == null;
  return (
    <>
      <div className={css(styles.schemaMetadataRow1)}>
        <div className={css(styles.schemaMetadataColumn)}>
          <div className={css(styles.schemaMetadataField)}>
            <SchemaNameInput
              isWritable={props.shouldAllowSchemaUpdates && !isSchemaArchived}
              name={props.name}
              onNameChange={props.onNameChange}
              schema={props.schema}
              dataTestAttrs={DATA_TEST_ATTRS.name}
            />
          </div>
        </div>
        <div className={css(styles.schemaMetadataColumn)}>
          <div className={css(styles.schemaMetadataField)}>
            <SchemaOwnerDisplay
              ownerId={props.ownerId}
              ownerChoices={props.ownerChoices}
              onOwnerChange={props.onOwnerChange}
              schema={props.schema}
              dataTestAttrs={DATA_TEST_ATTRS.owner}
            />
          </div>
        </div>
        <div className={css(styles.schemaMetadataColumn)}>
          {BenchlingConfig.ENABLE_WAREHOUSE && !props.hideWarehouseTableName && (
            <div className={css(styles.schemaMetadataField)}>
              <SchemaWarehouseNameInput
                isWritable={
                  props.shouldAllowSchemaUpdates && props.isEditableWarehouseTableName && !isSchemaArchived
                }
                schema={props.schema}
                sqlIdentifier={props.sqlIdentifier || ''}
                onSqlIdentifierChange={props.onSqlIdentifierChange}
                dataTestAttrs={DATA_TEST_ATTRS.warehouseTable}
              />
            </div>
          )}
        </div>
      </div>
      <div className={css(styles.schemaMetadataRow2)}>
        <div className={css(styles.schemaMetadataColumn)}>
          <div className={css(styles.schemaMetadataField)}>
            <SchemaPrefixInput
              isBeingCreated={isBeingCreated}
              isRequired={true}
              isWritable={props.shouldAllowSchemaUpdates && !isSchemaArchived}
              prefix={props.idPrefix}
              onPrefixChange={props.onIdPrefixChange}
            />
          </div>
        </div>
        <div className={css(styles.schemaMetadataColumn)}>
          <div className={css(styles.schemaMetadataField)}>
            <AdapterSelector
              value={props.adapterId ?? null}
              onChange={props.onAdapterIdChange}
              schema={props.schema}
            />
          </div>
        </div>
        <div className={css(styles.schemaMetadataColumn)} />
      </div>
    </>
  );
}

const rowStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
};

const styles = StyleSheet.create({
  ownerDropdown: {
    flex: 1,
  },
  schemaMetadataRow1: {
    ...rowStyles,
    marginBottom: '10px',
  },
  schemaMetadataRow2: {
    ...rowStyles,
    marginBottom: '32px',
  },
  schemaMetadataColumn: {
    flex: 0.3,
    minWidth: 0,
  },
  schemaMetadataField: {
    width: 'auto',
    marginBottom: '0',
  },
});
